import { useEffect } from "react";

// Installed as a PWA, there is no browser chrome around this app — Android's
// back gesture is the only way back, and by default it is also the way out:
// with nothing of its own in the history stack, one back press closes the
// app instead of whatever sheet is open on screen.
//
// A component that is only ever mounted while it is "open" — Detail, the
// bottom nav's menu, the assistant — calls this once, unconditionally, for
// as long as it exists. It pushes one history entry on mount, so the first
// back press consumes that entry and fires `onClose` instead of leaving the
// app; a second press then does whatever back would have done anyway.
//
// Closed from inside instead (a tap outside, a close button) rather than by
// the back button, the pushed entry is still sitting there unconsumed —
// popped on unmount so the two paths never leave the history stack one deep
// depending on which way the sheet closed.
//
// Two things make the push and the listener wait, both about sheets that
// open in the same breath as another closes:
//
//   · StrictMode (on in main.tsx) mounts every effect, unmounts it and mounts
//     it again in development. A synchronous push is popped by that first
//     cleanup's history.back(), and the popstate that raises would close the
//     sheet the second mount had just opened. Waiting a microtask lets the
//     first cleanup cancel before anything is pushed.
//   · A sheet that closes calls history.back(), which lands later. If another
//     sheet opens meanwhile — "Dive in" closes the task and opens the
//     assistant; the menu's "Ask AI" does the same — the late pop would close
//     the new sheet. So the next sheet waits for that pop to land (or for a
//     short timeout, if there was nothing to pop) before it pushes its own
//     entry or starts listening.
let settling: Promise<void> = Promise.resolve();

export function useCloseOnBack(onClose: () => void) {
  useEffect(() => {
    let alive = true;
    let pushed = false;
    const onPopState = () => onClose();
    settling.then(() => {
      if (!alive) return;
      history.pushState({ mcSheet: true }, "");
      pushed = true;
      window.addEventListener("popstate", onPopState);
    });
    return () => {
      alive = false;
      window.removeEventListener("popstate", onPopState);
      if (pushed && history.state?.mcSheet) {
        settling = new Promise<void>((resolve) => {
          const done = () => { window.removeEventListener("popstate", done); window.clearTimeout(t); resolve(); };
          const t = window.setTimeout(done, 300);
          window.addEventListener("popstate", done);
        });
        history.back();
      }
    };
    // Deliberately once, on mount/unmount, not re-run when `onClose` changes
    // identity — the entry belongs to the sheet's lifetime, not to a
    // particular render of the callback that closes it.
  }, []);
}
